import { RoleType } from '../../models/Role'
import { type PromoteResponse, type PromoteResult } from '../../store/auth/auth'
import { seatTypeLabels } from './roleChangeSeatImpact'

export interface PromoteVerdict {
  ok: boolean
  message?: string
  closesModal: boolean
  refreshesSeats: boolean
}

const FALLBACK = 'The seat could not be assigned. Try again.'
const TEMPORARY = 'The seat check is unavailable right now. Try again in a moment.'
const ALREADY_SENT = 'This invitation was already sent.'
const PAYMENT_METHOD_MISSING = 'A payment method is needed before a seat can be bought.'

const failure = (message: string, refreshesSeats = false): PromoteVerdict => ({ ok: false, message, closesModal: false, refreshesSeats })

const gone = (message: string): PromoteVerdict => ({ ok: false, message, closesModal: true, refreshesSeats: false })

const verdictFor = (result: PromoteResult, seatType: RoleType): PromoteVerdict => {
  if (result.outcome === 'promoted') {
    return { ok: true, closesModal: true, refreshesSeats: true }
  }

  switch (result.reason) {
    case 'noSeatAvailable':
      return failure(`No free ${seatTypeLabels[seatType]} seat is available any more.`, true)
    case 'seatCheckUnavailable':
    case 'roleCheckUnavailable':
      return failure(TEMPORARY)
    case 'paymentRequired':
      return failure(PAYMENT_METHOD_MISSING)
    case 'emailFailed':
      return failure('The invitation email could not be sent. The request is unchanged.')
    case 'roleUnavailable':
      return failure('The role on this request is no longer available. Revoke it and invite the user again.')
    case 'notFound':
      return gone('This request no longer exists.')
    case 'notRequested':
    case 'alreadyActive':
      return gone(ALREADY_SENT)
    default:
      return failure(FALLBACK, true)
  }
}

export const readPromoteResult = (response: PromoteResponse | undefined, invitationId: string, seatType: RoleType): PromoteVerdict => {
  const result = response?.results.find((row) => row.invitationId === invitationId)
  return result ? verdictFor(result, seatType) : failure(FALLBACK, true)
}
